import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TableWithSearch } from "@/components";
import type { DataTableFeatures } from "@/components/data-table/features";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import {
	useCurrentStore,
	usePaginatedSearch,
	usePaymentTypes,
} from "../../hooks";

const PaymentTypes = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("paymentTypes.pageTitle") }]);
	const currentStore = useCurrentStore();
	const { searchQuery, page, setPage, onSearchQueryChange } =
		usePaginatedSearch();
	const { data, isLoading } = usePaymentTypes({
		page,
		"q[nameOrDescriptionCont]": searchQuery,
		"q[storeIdEq]": currentStore?.id,
		"q[s]": ["name"],
	});

	const columnHelper = createColumnHelper<DataTableFeatures, PaymentType>();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor("name", {
					header: t("paymentTypes.table.name"),
					cell: (props) => (
						<Link
							className="underline"
							to={`/payment-types/${props.row.original.id}/edit`}
						>
							{props.getValue()}
						</Link>
					),
				}),
				columnHelper.accessor("description", {
					header: t("paymentTypes.table.description"),
				}),
			] as ColumnDef<DataTableFeatures, PaymentType>[],
		[t, columnHelper],
	);

	return (
		<TableWithSearch
			searchPlaceholder={t("paymentTypes.searchPlaceholder")}
			onSearchQueryChange={onSearchQueryChange}
			searchQuery={searchQuery}
			isLoading={isLoading}
			createUrl="/payment-types/new"
			createLabel={t("paymentTypes.createButtonLabel")}
			columns={columns}
			data={data?.data}
			currentPage={page}
			totalPages={data?.headers["total-pages"]}
			onPageChange={setPage}
		/>
	);
};

export default PaymentTypes;
